//Libs
import { Fragment, useEffect, useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";

//Local
import ZoneListPage from "./ZoneListPage";
import { ExistingZone, Zone_CreateAPI, ZoneManager } from "../../models/zone";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { logger } from "../../logging";
import { DbRead, DbWrite } from "../../database";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import * as strings from "../../strings";
import AddEditZoneDialog, {
  ZoneFormState,
} from "../../components/Zones/AddEditZoneDialog";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { useAuthStore } from "../../store/firebase-auth";
import { User } from "firebase/auth";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { DocumentData } from "firebase/firestore";
import { diffObjects } from "../../assets/js/object-diff";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { handleTags } from "../../assets/js/HandleTags";

interface Props {
  siteKey: string;
}

export default function ZoneListContainer({ siteKey }: Props) {
  // #region SECTION: Stores
  const firebaseUser = useAuthStore((state) => state.firebaseUser) as User;
  // const [getLocationTitle, locationStoreIsLoading, siteKeyLocationList] =
  //   useSiteKeyLocationsStore((state) => [
  //     state.getLocationTitle,
  //     state.loading,
  //     state.siteKeyLocationList,
  //   ]);
  const userPermissions = useUserPermissionsStore(
    (state) => state.siteKeyUserPermissions,
  );
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);
  // #endregion SECTION: Stores

  // #region SECTION: useState, useEffect

  const [zoneList, setZoneList] = useState<ExistingZone[]>([]);
  const [zoneListIsLoading, setZoneListIsLoading] = useState<boolean>(false);

  // Fetch the list of zones when this component loads.
  useEffect(() => {
    async function getZones() {
      logger.debug("getZones useEffect called ");
      // Query zones via realtime updates. Set the list of zones.
      const unsubscribeAllZones = DbRead.zones.subscribeAll({
        siteKey: siteKey,
        onChange: (zoneList: ExistingZone[]) => {
          setZoneList(zoneList), setZoneListIsLoading(false);
        },
        onError: (error) => logger.error(`Error in getZones: ${error.message}`),
      });
      return unsubscribeAllZones;
    }

    // Store the returned 'unsubscribe' ƒn into the unsubscribePromise variable.
    setZoneListIsLoading(true);
    const unsubscribePromise = getZones();
    // Return an anonymous ƒn for cleanup.
    return () => {
      logger.debug(
        "(ZoneListContainer) getZones useEffect return ƒn just invoked.",
      );
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) {
          logger.debug("Now unsubscribing from zones.");
          unsubscribe();
        }
      });
      logger.debug("Done running cleanup ƒn for getZones useEffect.");
    };
  }, [siteKey]);

  const [addEditZoneDialogOpen, setAddEditZoneDialogOpen] =
    useState<boolean>(false);
  const [selectedZone, setSelectedZone] = useState<ExistingZone | null>(null);

  // #endregion SECTION: useState, useEffect

  // #region SECTION: Functions

  async function handleEditZone(updateZone: ZoneFormState) {
    if (firebaseUser == null) {
      logger.error("firebaseUser is null");
      return;
    }
    if (!selectedZone) return;

    const partialUpdateZone: Partial<ExistingZone> = {
      ...updateZone,
      values: handleTags(updateZone.values),
      color: updateZone.color ?? selectedZone.color,
      property: updateZone.property ?? selectedZone.property,
    };

    const diffZoneValues: DocumentData = diffObjects(
      selectedZone,
      partialUpdateZone,
    ).diff;

    if (Object.keys(diffZoneValues).length === 0) {
      logger.debug("No values have changed");
      return;
    }

    diffZoneValues["id"] = selectedZone.id;
    diffZoneValues["refPath"] = selectedZone.refPath;
    diffZoneValues["lastModifiedBy"] = firebaseUser.uid;

    //Validate
    const validatedEditZone = ZoneManager.parseUpdate(diffZoneValues);
    logger.info("Validated Zone:", validatedEditZone);

    //DB
    try {
      await DbWrite.zones.update(validatedEditZone);
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulUpdate(selectedZone.name),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleEditZone`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  async function handleSaveNewZone(formValues: ZoneFormState) {
    if (firebaseUser == null) {
      logger.error("firebaseUser is null");
      return;
    }

    const newZone: Zone_CreateAPI = {
      ...formValues,
      color: formValues.color ?? "#000000",
      property: formValues.property ?? "",
      name: formValues.name,
      element: formValues.element,
      values: handleTags(formValues.values),
      createdBy: firebaseUser.uid,
      lastModifiedBy: firebaseUser.uid,
      siteKey: siteKey,
    };

    //Validate
    const validatedZone = ZoneManager.parseCreate(newZone);
    logger.info("Validated Zone:", validatedZone);

    //DB
    try {
      await DbWrite.zones.create(validatedZone);
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulAdd(validatedZone.name),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleSaveNewZone`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  async function handleDeleteZone(zoneID: ExistingZone["id"]) {
    if (!selectedZone) return;

    const title = selectedZone.name;

    try {
      logger.debug(`Deleting zone with ID: ${zoneID}`);

      await DbWrite.zones.delete(siteKey, zoneID);
      setAddEditZoneDialogOpen(false);
      setSelectedZone(null);
      logger.debug("Zone deleted successfully.");
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulDelete(title),
        dialog: false,
        type: "success",
      });
    } catch (error) {
      logger.error(`An error occurred during handleDeleteZone`, error);
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: false,
        type: "error",
      });
    }
  }

  function openEditZoneDialog(zone: ExistingZone) {
    setAddEditZoneDialogOpen(true);
    setSelectedZone(zone);
  }

  // #endregion SECTION: Functions

  // #region SECTION: Components

  const addNewZoneButton = (
    <BaseButtonPrimary
      type="button"
      onClick={() => setAddEditZoneDialogOpen(true)}
      className="w-full text-primary sm:w-48"
    >
      <AddCircleIcon fontSize="small" className="mr-2" />
      {strings.ADD_NEW_ZONE}
    </BaseButtonPrimary>
  );

  const addEditZoneDialog = userPermissions && siteKeyDoc && (
    <AddEditZoneDialog
      isDialogOpen={addEditZoneDialogOpen}
      closeDialog={() => {
        setAddEditZoneDialogOpen(false);
        setSelectedZone(null);
      }}
      handleEditZone={handleEditZone}
      handleSaveNewZone={handleSaveNewZone}
      handleDeleteZone={handleDeleteZone}
      userPermissions={userPermissions}
      zoneDoc={selectedZone}
      siteKeyDoc={siteKeyDoc}
    />
  );

  // #endregion SECTION: Components

  /* RENDER LOADING */
  if (zoneListIsLoading || siteKeyDocIsLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  return (
    <Fragment>
      <ZoneListPage
        zoneList={zoneList}
        addNewZoneButton={addNewZoneButton}
        onEditZone={openEditZoneDialog}
      />
      {addEditZoneDialog}
    </Fragment>
  );
}
